import { SEO_AUDIT_METADATA } from "./seoAuditMetadata";
/**
 * Centralized SEO metadata. Source of truth for page titles, descriptions,
 * and canonical URL construction. Consumed by SEOManager on every route
 * change so that each page emits its own self-referencing canonical and
 * unique title/description.
 */

// Relative, NOT "@/…": scripts/prerender-seo.mjs and the SEO guards load this
// file through jiti WITHOUT the "@" alias configured, so an aliased import
// here fails at prerender time rather than at type-check time.
import { ESA_LETTER_CLUSTER } from "../seo/blog/esaLetterCluster";
import { ESA_HOUSING_GUIDES } from "../seo/blog/esaHousingGuides";
import { SCHEDULED_ARTICLES } from "../seo/blog/scheduledArticles";
import { LEAD_MAGNET_ARTICLES } from "../seo/blog/leadMagnetArticles";

// ── 2026-05-21 SEO-DOMAIN-CANONICAL ─────────────────────────────────────────
// Single source of truth for the canonical host.
//
// PawTenant historically lived on both `pawtenant.com` (non-www, used by the
// original WordPress site and registered with Google Search Console) and
// `www.pawtenant.com` (used by an earlier Vercel deployment of the React
// build). Two indexable hosts splits link equity, creates duplicate-canonical
// reports in SEO tools, and produces the "Canonical defined multiple times"
// warning when the per-page JSX meta and the prerendered <head> disagree.
//
// We have now standardized on `https://pawtenant.com` (NON-WWW) as the only
// canonical and indexable host. The 301 redirect from www.* → non-www lives in
// `vercel.json`; everything that consumes BASE_URL (SEOManager runtime
// canonicals, scripts/prerender-seo.mjs build-time canonicals, all og:url
// fields) inherits the host from this constant. Do not hardcode the host
// anywhere else in the SEO pipeline — import BASE_URL instead.
export const BASE_URL = "https://pawtenant.com";

export interface SEOEntry {
  title: string;
  description: string;
  keywords?: string;
}

// ── ESA-letter blog cluster ─────────────────────────────────────────────────
// PAWTENANT-BLOG-SEO-CLUSTER-LIVE-ROLLOUT-002. The six cluster articles keep
// their title and description in ONE place — the same record the pages render
// their FAQs and schema from — so there is no second copy here to fall out of
// date.
const ESA_LETTER_CLUSTER_SEO: Record<string, SEOEntry> = Object.fromEntries(
  Object.values(ESA_LETTER_CLUSTER).map((a) => [
    a.route,
    { title: a.metaTitle, description: a.metaDescription },
  ]),
);

// ── ESA housing guides ──────────────────────────────────────────────────────
// PAWTENANT-ESA-HOUSING-GUIDES-PUBLISH-001. Same single-record rule: the three
// guides' titles and descriptions (the owner-approved PDFs' own) live in
// src/seo/blog/esaHousingGuides.ts only.
const ESA_HOUSING_GUIDES_SEO: Record<string, SEOEntry> = Object.fromEntries(
  Object.values(ESA_HOUSING_GUIDES).map((g) => [
    g.route,
    { title: g.metaTitle, description: g.metaDescription },
  ]),
);

// ── Lead-magnet articles ────────────────────────────────────────────────────
// BLOG-LEAD-MAGNET-SAMPLES-001. Titles and descriptions live in
// src/seo/blog/leadMagnetArticles.ts only (same single-record rule).
const LEAD_MAGNET_ARTICLES_SEO: Record<string, SEOEntry> = Object.fromEntries(
  LEAD_MAGNET_ARTICLES.map((a) => [a.route, { title: a.metaTitle, description: a.metaDescription }]),
);

// ── AI-SEO-ESA-LETTER-COST-ANSWER-INTENT-001 ────────────────────────────────
// The /esa-letter-cost title and description carry REAL prices so the SERP
// snippet answers "how much does an ESA letter cost?" before the click. The
// numbers are interpolated from src/config/pricing.ts — the same canonical
// matrix that feeds the price cards, the hero chip and Stripe checkout — so a
// price change updates the snippet automatically and the meta can never drift
// away from what the customer is actually charged. Never hardcode a literal
// price in this file.
import { ESA_PRICE_LABELS } from "./pricing";

const ESA_COST_ANNUAL = ESA_PRICE_LABELS.subscription; // "$115" (first year)
const ESA_COST_ONETIME = ESA_PRICE_LABELS.oneTime; // "$129"

// ── Core static pages ────────────────────────────────────────────────────────
export const CORE_PAGE_META: Record<string, SEOEntry> = {
  "/": {
    title: "Emotional Support Animal Letter Online | PawTenant",
    description:
      "Get an emotional support animal letter through a licensed provider evaluation: a verifiable, housing-focused letter and a refund if you don't qualify.",
  },
  "/assessment": {
    title: "ESA Letter Assessment | Start Online | PawTenant",
    description:
      "Begin your ESA letter application in minutes. Answer a short questionnaire, connect with a licensed LMHP, and receive your ESA letter fast.",
  },
  // AI-SEO-ESA-LETTER-HEAD-TERM-CONSOLIDATION-001 — this route is the single
  // canonical organic owner of the "esa letter" / "how to get an esa letter"
  // informational-commercial cluster (30 clicks, 1,143 impressions, GSC 28d to
  // 2026-08-13 — the strongest of the four URLs Google was splitting it
  // between). Title now leads with the head term itself and carries the brand.
  "/how-to-get-esa-letter": {
    title: "How to Get an ESA Letter: Step-by-Step | PawTenant",
    description:
      "How to get an ESA letter step by step: a licensed provider evaluation, a verifiable housing-focused letter, and a refund if you don't qualify.",
      keywords: 
      "How to get ESA letter,Get esa letter,Get ESA letter online ,How to get ESA letter online",
  },
  "/housing-rights-esa": {
    title: "ESA Housing Rights Explained | Fair Housing Act | PawTenant",
    description:
      "Understand your housing rights with an emotional support animal. Learn how the Fair Housing Act protects ESA owners and keeps you and your pet together.",
  },
  "/esa-letter-housing": {
    title: "ESA Letter for Housing | Licensed Review | PawTenant",
    description:
      "Get an ESA letter for housing after a confidential assessment by a professional licensed in your state. Issued only if appropriate; refund if not.",
  },
  // GOOGLE-ADS-LANDING-PAGES-001 — the seven ad-group landing pages. Prerendered
  // and self-canonical, but intentionally NOT in public/sitemap.xml until the owner
  // approves them for production. Prices are interpolated, never typed.
  "/esa-letter-online": {
    title: "ESA Letter Online From a Licensed Professional | PawTenant",
    description:
      "Start a confidential online ESA evaluation with a professional licensed in your state. A letter is issued only if appropriate; full refund if you don't qualify.",
  },
  "/esa-letter-for-renters": {
    title: "ESA Letter for Renters & Apartments | PawTenant",
    description:
      "ESA documentation for a reasonable accommodation request to your landlord, from a professional licensed in your state. What it can and can't do in 2026.",
  },
  "/legitimate-esa-letter": {
    title: "Legitimate ESA Letter | Licensed & Verifiable | PawTenant",
    description:
      "How to tell a legitimate ESA letter from a fake, what a valid letter includes, and how landlords verify a PawTenant letter without seeing health details.",
  },
  "/psychiatric-service-dog-evaluation": {
    title: "Psychiatric Service Dog Evaluation & PSD Letter | PawTenant",
    description:
      "Licensed online evaluation for handlers whose dog is trained to perform disability-related tasks. PSD documentation only: no training, registry or certificate.",
  },
  "/emotional-support-cat-letter": {
    title: "Emotional Support Cat Letter for Housing | PawTenant",
    description:
      "Housing-focused ESA documentation for your cat after a licensed professional's evaluation. How emotional support cats fit the process, and the limits.",
  },
  "/esa-letter-cost": {
    title: `Affordable ESA Letter Online | From ${ESA_COST_ANNUAL} | PawTenant`,
    description:
      `Affordable ESA letter for housing from ${ESA_COST_ANNUAL} for the first year or ${ESA_COST_ONETIME} one-time. Licensed clinical review. Full refund if you don't qualify.`,
  },
  // PAWTENANT-BING-INDEXING-HYGIENE-001. A public, index,follow page that had no
  // prerendered file, so it was served the fallback app shell — which is now
  // noindex. Registering it gives it its own prerendered, self-canonical,
  // indexable <head>. Title and description are BYTE-IDENTICAL to the page's own
  // JSX (src/pages/psd-letter-cost/page.tsx) — no copy change.
  "/psd-letter-cost": {
    title: "PSD Letter Cost: Psychiatric Service Dog Documentation Pricing | PawTenant",
    description:
      "Psychiatric service dog (PSD) letter cost explained: transparent pricing for a licensed provider evaluation, a Reasonable Accommodation option, and a refund if you don't qualify.",
  },
  "/pet-rent-savings-calculator": {
    title: "Pet Rent Savings Calculator | Estimate ESA Housing Savings",
    description:
      "Use our pet rent savings calculator to see how pet rent and fees add up over 1, 2, and 5 years if an ESA accommodation is approved.",
  },
  // ── Provider directory + curated provider profiles ─────────────────────────
  // AI-SEO-PROVIDER-CANONICAL-DEDUP-AND-EXPANSION-001. Unique title/description
  // per provider; verified facts only; no approximate state counts; no "Dr.".
  "/our-providers": {
    title: "Our Licensed ESA Providers | PawTenant",
    description:
      "Meet our licensed ESA providers. Each conducts individual telehealth ESA and PSD evaluations only in the states where they hold a license.",
  },
  "/doctors/robert-staaf": {
    title: "Robert Staaf, LCSW | ESA Evaluations | PawTenant",
    description:
      "Robert Staaf is a Licensed Clinical Social Worker (LCSW) providing individual telehealth ESA evaluations through PawTenant. NPI-verified.",
  },
  // LIVE-PUBLIC-PAGES-...-PROVIDER-FIX-001 — /doctors/michelle-lafferty has NO
  // SEO entry while Admin has this provider inactive + unpublished.
  //
  // With an entry here, prerender-seo.mjs emitted a static shell whose raw HTML
  // said "index, follow" and whose <title>/<description> named her, even though
  // the body renders a privacy-safe not-found. That advertised an unpublished
  // provider to crawlers and leaked her name into the tab title.
  //
  // The ROUTE still resolves (classifier keeps all eight slugs) and renders
  // not-found + noindex. To re-publish: flip her status in Admin, re-verify
  // snapshotActive/snapshotPublished in src/data/publicProviders.ts, and restore
  // a title/description entry here.
  "/doctors/lytara-garcia": {
    title: "Lytara Garcia, LCSW | ESA Evaluations | PawTenant",
    description:
      "Lytara Garcia is a Licensed Clinical Social Worker (LCSW) providing individual telehealth ESA evaluations through PawTenant. NPI-verified.",
  },
  // PROVIDER-CAROUSEL-STEPHANIE-IMAGE-ORDER-LIVE-001 — /doctors/stephanie-white
  // has NO SEO entry for the same reason as michelle-lafferty above: she is
  // unpublished in Admin and not approved for public display. The route still
  // resolves to a privacy-safe not-found with noindex.
  "/doctors/eve-rosno": {
    title: "Eve Rosno, Licensed Psychologist | PawTenant",
    description:
      "Eve Rosno is a licensed psychologist providing individual telehealth ESA evaluations through PawTenant. NPI-verified.",
  },
  "/doctors/henry-smith": {
    title: "Henry Smith, LCSW | ESA Evaluations | PawTenant",
    description:
      "Henry Smith is a Licensed Clinical Social Worker (LCSW) providing individual telehealth ESA evaluations through PawTenant. NPI-verified.",
  },
  "/doctors/chad-cunningham": {
    title: "Chad Cunningham, LPC | ESA Evaluations | PawTenant",
    description:
      "Chad Cunningham is a Licensed Professional Counselor (LPC) providing individual telehealth ESA evaluations through PawTenant. NPI-verified.",
  },
  "/doctors/karla-delgado": {
    title: "Karla Delgado, LMFT | ESA Evaluations | PawTenant",
    description:
      "Karla Delgado is a Licensed Marriage and Family Therapist (LMFT) providing individual telehealth ESA evaluations through PawTenant. NPI-verified.",
  },
  "/doctors/cassandra-enriquez": {
    title: "Cassandra Enriquez, LPC | ESA Evaluations | PawTenant",
    description:
      "Cassandra Enriquez is a Licensed Professional Counselor (LPC) providing individual telehealth ESA evaluations through PawTenant. NPI-verified.",
  },
  "/explore-psd-letters-all-states": {
    title: "PSD Letters by State | State Guide Directory | PawTenant",
    description: "Explore all published PawTenant psychiatric service dog state guides. Find your state guide and check provider availability with our support team.",
  },
  "/explore-esa-letters-all-states": {
    title: "ESA Letter by State | All 50 US States | PawTenant",
    description:
      "Find ESA letter requirements for your state. PawTenant connects you with licensed professionals in all 50 US states. Know your rights and get your letter today.",
  },
  // ── ESA laws / compliance content pages (informational, indexable) ─────────
  "/esa-laws": {
    title: "ESA Laws & ESA Letter Requirements Explained | PawTenant",
    description:
      "ESA laws explained: how the Fair Housing Act protects emotional support animal owners, what a valid letter requires, and what it can't do.",
  },
  "/are-online-esa-letters-legit": {
    title: "Are Online ESA Letters Legit? | PawTenant",
    description:
      "How to tell if an online ESA letter is real: a licensed provider evaluation, a named provider, and a verifiable letter — and how to avoid fake registrations.",
  },
  "/california-esa-letter-30-day-rule": {
    title: "California ESA Letter & the 30-Day Rule (AB 468) | PawTenant",
    description:
      "Getting a California ESA letter: AB 468 requires a 30-day provider relationship and a clinical evaluation first. Here's how to comply.",
  },
  "/iowa-esa-letter-housing-rules": {
    title: "Iowa ESA Letter & Housing Rules (§ 216.8B) | PawTenant",
    description:
      "Iowa ESA letter rules: Code § 216.8B and SF 2268 protect assistance animals in housing. What Iowa landlords can ask and what documents need.",
  },
  "/florida-esa-letter-housing-rules": {
    title: "Florida ESA Letter & Housing Rules (§ 760.27) | PawTenant",
    description:
      "Florida Statute 760.27 sets the rules for ESA documentation in housing — who can write it and what a landlord can ask. Learn how to stay compliant in Florida.",
  },
  "/landlord-denied-esa-letter": {
    title: "Landlord Denied ESA Letter? Next Steps | PawTenant",
    description:
      "Landlord denied your ESA? Calm next steps, your options, and how a licensed provider letter with landlord-ready support can help. Refund if you don't qualify.",
  },
  // ── AI-SEO answer-library pages (high-intent ESA queries) ──────────────────
  "/best-online-esa-letter-service": {
    title: "Best Online ESA Letter Service: How to Choose",
    description:
      "How to choose a real online ESA letter service: licensed provider evaluation, transparent pricing, verifiable letters, and housing-focused support.",
  },
  "/how-to-get-esa-letter-online": {
    title: "How to Get an ESA Letter Online | PawTenant",
    description:
      "Get an ESA letter online via a licensed provider evaluation: a fast telehealth assessment, a verifiable letter for housing, and a refund if you don't qualify.",
  },
  "/esa-letter-for-landlord": {
    title: "ESA Letter for Landlords: Housing Request Guide | PawTenant",
    description:
      "Use an ESA letter with your landlord: housing-focused documentation from a licensed provider, what to send, and how landlords can verify it.",
  },
  "/esa-pet-rent-deposit": {
    title: "ESA Pet Rent & Deposit Guide: What Landlords Can Charge",
    description:
      "Learn when ESA pet rent, deposits, and fees may be removed, what landlords can still ask, and how PawTenant supports housing ESA documentation.",
  },
  "/how-to-verify-esa-letter": {
    title: "How to Verify an ESA Letter Online | Real ESA Letter Guide",
    description:
      "Learn how to verify an ESA letter, check licensed provider details, avoid fake ESA registrations, and use PawTenant's housing documentation support.",
  },
  "/esa-psd-registry-vs-letter": {
    title: "ESA Registry vs Letter: What Landlords Need | PawTenant",
    description:
      "ESA registry vs letter: why registries, certificates, and ID cards fall short, and what provider-reviewed documentation landlords accept.",
  },
  "/is-pawtenant-legit": {
    title: "Is PawTenant Legit? How the ESA Letter Service Works",
    description:
      "Is PawTenant legit? We connect you with licensed providers for ESA letters, with transparent pricing, verifiable letters, and a refund policy.",
  },
  // ── Landlord ESA objection / documentation SEO batch (long-tail housing intent) ──
  "/can-landlord-reject-esa-letter": {
    title: "Can a Landlord Reject an ESA Letter? Your Housing Rights",
    description:
      "Can a landlord reject an ESA letter? When a housing provider may deny a reasonable-accommodation request, when they generally can't, and how to respond.",
  },
  "/what-documents-can-landlord-ask-for-esa": {
    title: "What Documents Can a Landlord Ask For an ESA?",
    description:
      "The documents a landlord can ask for with an ESA: what housing providers can reasonably request, what they can't, and keeping medical details private.",
  },
  "/esa-letter-vs-pet-policy": {
    title: "ESA Letter vs Pet Policy: Does a No-Pet Rule Apply?",
    description:
      "ESA letter vs pet policy: does a no-pet rule apply to an emotional support animal, can apartments charge ESA pet rent, and how requests work.",
  },
  "/how-to-respond-to-esa-letter-denial": {
    title: "How to Respond if Your Landlord Denies Your ESA Letter",
    description:
      "Facing an ESA letter denial or told it's fake? Calm next steps, what to send your landlord, how verification helps, and the support available.",
  },
  // ── ESA letter validity / verification SEO batch (long-tail validity intent) ──
  "/what-makes-esa-letter-valid": {
    title: "What Makes an ESA Letter Valid for Housing?",
    description:
      "What makes an ESA letter valid for housing: a licensed provider evaluation, what must be included, whether online letters count, and how a landlord checks it.",
  },
  "/landlord-says-esa-letter-is-fake": {
    title: "Landlord Says My ESA Letter Is Fake — What to Do",
    description:
      "Landlord says your ESA letter is fake? How to show it's genuine, the warning signs of a truly fake letter, and how to respond with verification.",
  },
  "/esa-letter-verification-id": {
    title: "ESA Letter Verification: What It Is & Why It Helps",
    description:
      "What an ESA letter verification ID is, and how a landlord uses it in our portal to confirm a letter is genuine without seeing medical details.",
  },
  // ── ESA housing SEO batch (apartments / accommodation request / landlord checklist) ──
  "/esa-letter-for-apartments": {
    title: "ESA Letter for Apartments | Housing Accommodation Guide",
    description:
      "How an ESA letter works for apartment renters: a licensed provider evaluation, what to send your property manager, pet fees, verification, and denial support.",
  },
  "/esa-accommodation-request-letter": {
    title: "ESA Accommodation Request Letter Template | PawTenant",
    description:
      "Write a clear ESA accommodation request letter for your landlord: what to include, a simple template, and how it pairs with your ESA letter.",
  },
  "/landlord-esa-documentation-checklist": {
    title: "Landlord ESA Documentation Checklist | What to Review",
    description:
      "A landlord ESA documentation checklist: what you can request, what you can't ask, and how to verify a letter without seeing medical details.",
  },
  // ── State apartment ESA SEO batch (apartment-renter housing intent by state) ──
  "/california-esa-letter-for-apartments": {
    title: "California ESA Letter for Apartments | Renter Housing Guide",
    description:
      "A California ESA letter for apartments: how AB 468's 30-day rule, a licensed evaluation, and a verifiable letter support your housing request.",
  },
  "/texas-esa-letter-for-apartments": {
    title: "Texas ESA Letter for Apartments | Renter Housing Guide",
    description:
      "A Texas ESA letter for apartments: how a licensed evaluation and a verifiable letter support an accommodation request with property managers.",
  },
  "/florida-esa-letter-for-apartments": {
    title: "Florida ESA Letter for Apartments | Renter Housing Guide",
    description:
      "A Florida ESA letter for apartments and condos: Statute 760.27, a licensed provider evaluation, and a verifiable letter for your request.",
  },
  "/new-york-esa-letter-for-apartments": {
    title: "New York ESA Letter for Apartments | Renter Housing Guide",
    description:
      "A New York ESA letter for apartments, co-ops, and condos: how a licensed evaluation and verifiable letter support your accommodation request.",
  },
  // ── PSD AEO content batch (PSD conversion + AI-answer pages) ──────────────────
  "/psychiatric-service-dog-letter-online": {
    title: "Psychiatric Service Dog Letter Online | PawTenant",
    description:
      "Get a psychiatric service dog letter online through a licensed provider evaluation. Who qualifies, how it differs from an ESA, and the secure process.",
  },
  "/psd-letter-for-apartments": {
    title: "PSD Letter for Apartments and Housing Requests | PawTenant",
    description:
      "How a PSD letter for apartments supports a housing request: what a landlord can ask, Fair Housing rules, and when an ESA letter fits better.",
  },
  "/psd-letter-requirements": {
    title: "PSD Letter Requirements: What You Need to Know | PawTenant",
    description:
      "PSD letter requirements: a qualifying psychiatric disability, a task-trained dog, a licensed provider evaluation, and what documentation can do.",
  },
  "/esa-vs-psd-letter": {
    title: "ESA Letter vs PSD Letter: What's the Difference? | PawTenant",
    description:
      "ESA letter vs PSD letter compared: emotional support animal vs psychiatric service dog, what each covers for housing, the task-training rule, and how to choose.",
  },
  "/can-a-landlord-deny-a-psd-letter": {
    title: "Can a Landlord Deny a PSD Letter? | PawTenant",
    description:
      "Can a landlord deny a PSD letter? What they can verify, the limited valid reasons for denial, and the calm steps to take if it happens to you.",
  },
  "/do-you-need-a-psd-letter-for-a-service-dog": {
    title: "Do You Need a PSD Letter for a Service Dog? | PawTenant",
    description:
      "Do you need a PSD letter? Service dogs aren't certified by online registries, but a licensed provider's documentation can help with housing.",
  },
  "/are-esa-letters-still-valid-after-hud-change": {
    title: "Are ESA Letters Still Valid After the 2026 HUD Change?",
    description:
      "Are ESA letters still valid after HUD's 2026 change? What changed, what didn't, ESA vs trained PSD, and how a licensed evaluation helps.",
  },
  "/all-about-service-dogs": {
    title: "Service Dogs: PSD Letter vs ESA Letter | PawTenant",
    description:
      "Everything about service dogs: how a psychiatric service dog letter differs from an ESA letter, who may qualify, and how an evaluation works.",
  },
  "/how-to-get-psd-letter": {
    title: "How to Get a PSD Letter | PawTenant",
    description:
      "How to get a PSD letter (psychiatric service dog) through a licensed provider evaluation. Learn how a PSD differs from an ESA and whether you may qualify.",
  },
  "/about-us": {
    title: "About PawTenant | Our Network of Licensed Professionals",
    description:
      "About PawTenant: a network of licensed mental health providers putting clinical integrity and fair housing compliance first in every evaluation.",
  },
  "/no-risk-guarantee": {
    title: "PawTenant No-Risk Guarantee | ESA Letter Refund",
    description:
      "If a licensed provider determines you don't qualify, you get a full refund. Housing-denial refund requests are reviewed under PawTenant's Refund Policy.",
  },
  "/faqs": {
    title: "ESA FAQs: Your ESA Letter Questions Answered | PawTenant",
    description:
      "ESA FAQs answered: letters, housing rights, college dorms, air travel, renewals, and verification, from PawTenant's licensed provider network.",
  },
  "/college-pet-policy": {
    title: "ESA Letter for College Dorms: 2026 Guide | PawTenant",
    description:
      "An ESA letter for college can help you keep an emotional support animal in university housing. How dorm requests work and how to get one.",
  },
  "/airline-pet-policy": {
    title: "Airline ESA Policy & Pet Rules 2026 | PawTenant",
    description:
      "Each airline ESA policy for 2026: Delta, United, American, Southwest and more, plus when a psychiatric service dog letter applies to air travel.",
  },
  "/service-animal-vs-esa": {
    title: "Service Animal vs ESA vs Therapy Dog | PawTenant",
    description:
      "Service animal vs ESA vs therapy dog: compare legal rights, training, housing protections, and airline access side by side for 2026.",
  },
  // AI-SEO-ESA-LETTER-HEAD-TERM-CONSOLIDATION-001 — the /blog INDEX was one of
  // four PawTenant URLs Google was splitting the "esa letter" head term across
  // (29 of 89 impressions, avg position 30.4, 0 clicks — GSC 28d to 2026-08-13).
  // A listing page cannot satisfy that query, so the title now leads with what
  // the page actually is (a resource library) instead of the head term itself.
  // The blog stays fully indexable — only its head-term relevance is reduced.
  "/blog": {
    title: "PawTenant Resource Library | ESA & PSD Guides by State",
    description:
      "Browse PawTenant's guides on emotional support animal and psychiatric service dog documentation, housing rules, and state-by-state tenant rights.",
  },
  "/blog/2026-hud-esa-guidelines": {
    title: "2026 HUD ESA Guidelines: Is Your ESA Letter Still Valid?",
    description:
      "The 2026 HUD ESA guidelines may change how untrained ESAs are handled in housing complaints. What changed, what rights remain, and PSD differences.",
  },
  // ── California ESA/PSD content cluster — PSD-training blog post ──────────────
  "/blog/how-to-train-psychiatric-service-dog-tasks": {
    title: "Psychiatric Service Dog Tasks: How to Train | PawTenant",
    description:
      "Psychiatric service dog tasks under the ADA: which tasks qualify, how self-training works legally, public-access standards, and documentation.",
  },
  // ── California ESA/PSD content cluster — /states/* regional guides ───────────
  "/states/california-esa-psd-guide": {
    title: "California ESA & PSD Guide: AB 468 | PawTenant",
    description:
      "California ESA & PSD law: AB 468's 30-day rule, ESA vs PSD differences, what landlords and HOAs can and can't do, and what a letter needs.",
  },
  "/states/los-angeles-esa-landlord-guide": {
    title: "Los Angeles Landlord ESA Rules: RSO & AB 468 | PawTenant",
    description:
      "Los Angeles landlord ESA rules: how the RSO and Just Cause Ordinances layer on AB 468, from no-pet policies to pet fees and eviction.",
  },
  "/states/san-francisco-hoa-psd-guide": {
    title: "San Francisco HOA Rules & PSD Rights | PawTenant",
    description:
      "How Davis-Stirling, FEHA, and the ADA limit San Francisco HOA pet rules (breed bans, weight caps, fees) for residents with a psychiatric service dog.",
  },
  "/states/san-diego-telehealth-guide": {
    title: "San Diego Telehealth ESA Guidelines Under AB 468 | PawTenant",
    description:
      "San Diego telehealth ESA evaluations under AB 468: California licensure, the 30-day rule, required disclosures, and how to check a provider.",
  },
  // ── Texas ESA/PSD content cluster ───────────────────────────────────────────
  "/states/texas-esa-psd-guide": {
    title: "Texas ESA & PSD Housing Laws 2026 | PawTenant",
    description:
      "Texas ESA & PSD housing law: no state waiting period, the 2026 HUD enforcement shift, fee exemptions, and misrepresentation penalties.",
  },
  "/blog/texas-service-animal-laws-penalties": {
    title: "Texas Service Animal Laws & Penalties for Misrepresentation | PawTenant",
    description:
      "What Texas law says about service animals vs emotional support animals — the ADA-style task standard, the strengthened HB 4164 misrepresentation penalty (up to $1,000 + community service), and what a business or landlord may ask.",
  },
  // ── Core ESA-letter blog cluster (how-to / what-is / requirements) ──────────
  "/blog/how-to-get-an-esa-letter-online": {
    title: "How to Get an ESA Letter Online in 2026: Step-by-Step Guide",
    description:
      "How to get a legitimate ESA letter online in 2026: who qualifies, the licensed evaluation, cost, timing, housing rights, and avoiding scam registries.",
  },
  "/blog/what-is-an-esa-letter": {
    title: "What Is an ESA Letter? 2026 Legal Requirements & Guide",
    description:
      "What is an ESA letter? What a valid one contains, who can write it, and what protections it still gives after HUD's 2026 enforcement change.",
  },
  // ── ESA-letter cluster (PAWTENANT-BLOG-SEO-CLUSTER-LIVE-ROLLOUT-002) ──────
  // Spread from src/seo/blog/esaLetterCluster.ts rather than retyped, so a
  // page's <title>, its prerendered <head>, its Open Graph title and its schema
  // headline cannot drift apart. /blog/esa-letter-requirements is in this set
  // too — its entry used to be written out by hand right here.
  ...ESA_LETTER_CLUSTER_SEO,
  // ── ESA housing guides (PAWTENANT-ESA-HOUSING-GUIDES-PUBLISH-001) ─────────
  ...ESA_HOUSING_GUIDES_SEO,
  // ── Lead-magnet articles (BLOG-LEAD-MAGNET-SAMPLES-001) ───────────────────
  ...LEAD_MAGNET_ARTICLES_SEO,
  ...Object.fromEntries(SCHEDULED_ARTICLES.map(a => [a.route, { title: `${a.title} | PawTenant`, description: a.description }])),
  // ── Pet-rent blog cluster ─────────────────────────────────────────────────
  "/blog/pet-rent-explained": {
    title: "Pet Rent Explained: What Renters Should Know | PawTenant",
    description:
      "What pet rent is, how it differs from a pet deposit and pet fee, how much it can add up to over time, and how housing-focused ESA documentation fits in.",
  },
  "/blog/apartment-pet-rent-and-esa-letters": {
    title: "Apartment Pet Rent and ESA Letters: What Renters Should Know",
    description:
      "How apartment pet rent works, when an approved emotional support animal can't be charged pet fees, and how to make an accommodation request.",
  },
  "/blog/pet-deposit-vs-pet-rent": {
    title: "Pet Deposit vs. Pet Rent: What Is the Difference?",
    description:
      "Pet deposit vs pet rent vs pet fee: what each is, which are refundable, how they add up, and how approved ESA documentation changes the picture.",
  },
  // ── State pet-rent blog cluster ───────────────────────────────────────────
  "/blog/california-pet-rent-and-esa-letters": {
    title: "California Pet Rent & ESA Letters | PawTenant",
    description:
      "How California pet rent works, the state's AB 468 ESA rule, and when an approved emotional support animal can't be charged pet fees.",
  },
  "/blog/new-york-pet-rent-and-esa-letters": {
    title: "New York Pet Rent and ESA Letters: What Renters Should Know",
    description:
      "How New York pet rent works, co-op and condo board review, and when an approved emotional support animal can't be charged pet fees.",
  },
  "/blog/florida-pet-rent-and-esa-letters": {
    title: "Florida Pet Rent and ESA Letters: What Renters Should Know",
    description:
      "How Florida pet rent works in apartments and condos, Statute 760.27's ESA rule, and when an approved ESA can't be charged pet fees.",
  },
  "/blog/texas-pet-rent-and-esa-letters": {
    title: "Texas Pet Rent and ESA Letters: What Renters Should Know",
    description:
      "How pet rent works in Texas apartments and large corporate-managed buildings, and when an approved emotional support animal may not be charged pet fees.",
  },
  "/blog/washington-pet-rent-and-esa-letters": {
    title: "Washington Pet Rent & ESA Letters | PawTenant",
    description:
      "How Washington pet rent works in Seattle-area rentals, the state's assistance-animal protections, and when an approved ESA can't be charged fees.",
  },
  "/blog/colorado-pet-rent-and-esa-letters": {
    title: "Colorado Pet Rent and ESA Letters: What Renters Should Know",
    description:
      "How pet rent works in Colorado's Denver-area rental market, the state's assistance-animal protections, and when an approved ESA may not be charged pet fees.",
  },
  // ── PSD blog cluster ───────────────────────────────────────────────────────
  "/blog/psychiatric-service-dog-letter-explained": {
    title: "Psychiatric Service Dog Letters Explained | PawTenant",
    description:
      "Psychiatric service dog letters in plain English: what they are, who qualifies, how they differ from an ESA letter, and when they actually help.",
  },
  "/blog/psd-letter-vs-service-dog-certificate": {
    title: "PSD Letter vs Service Dog Certificate | PawTenant",
    description:
      "PSD letter vs service dog certificate: why online certificates carry no legal weight, and what a licensed provider's documentation actually does.",
  },
  "/blog/psd-letter-for-anxiety": {
    title: "Can You Get a PSD Letter for Anxiety?",
    description:
      "Can you get a PSD letter for anxiety? Yes, if it rises to a disability and your dog performs trained tasks. Here's how the evaluation works.",
  },
  "/blog/psychiatric-service-dog-housing-rights": {
    title: "Psychiatric Service Dog Housing Rights | PawTenant",
    description:
      "Psychiatric service dog housing rights for renters: Fair Housing Act protections, what landlords can ask, no pet fees, and how to request.",
  },
  // ── PSD/ESA condition content cluster — anxiety/depression qualification ─────
  "/blog/can-anxiety-qualify-you-for-a-psd": {
    title: "Can Anxiety Qualify You for a Psychiatric Service Dog?",
    description:
      "A psychiatric service dog for anxiety is possible if it rises to a disability and the dog is task-trained. PSD vs ESA, plus ADA and housing rules.",
  },
  "/blog/can-depression-qualify-psychiatric-service-dog": {
    title: "Can Depression Qualify You for a Psychiatric Service Dog?",
    description:
      "A psychiatric service dog for depression is possible when it's disabling and the dog is task-trained. Task examples, ADA rules, and documentation.",
  },
  "/blog/can-depression-qualify-you-for-an-esa": {
    title: "Can Depression Qualify You for an ESA?",
    description:
      "An ESA for depression may be appropriate when a licensed professional confirms it limits a major life activity. Symptoms, letter rules, and HUD 2026.",
  },
  // ── Travel-anxiety / major-event ESA content cluster ───────────────────────
  "/travel-anxiety-esa-letter": {
    title: "ESA Letter for Travel Anxiety | PawTenant",
    description:
      "How an ESA letter for travel anxiety and temporary housing works, how an emotional support animal may help, and what the letter can't do.",
  },
  "/blog/emotional-support-animal-travel-anxiety": {
    title: "ESA for Travel Anxiety: Can It Help? | PawTenant",
    description:
      "Can an ESA for travel anxiety help on flights and road trips? How it may ease stress, plus the legal limits: ESA rights center on housing.",
  },
  "/blog/temporary-housing-emotional-support-animal": {
    title: "ESA in Temporary Housing & Extended Stays | PawTenant",
    description:
      "Can you keep an ESA in temporary housing, extended stays, or short-term rentals? What the Fair Housing Act covers and how policies differ.",
  },
  "/blog/crowds-travel-stress-emotional-support-animal": {
    title: "Crowds, Travel Stress & ESAs | PawTenant",
    description:
      "Crowds and travel stress can overwhelm anyone. How an emotional support animal may help you stay grounded, and why an ESA isn't venue access.",
  },
  "/contact-us": {
    title: "Contact PawTenant | ESA Letter Support — Email & Phone Help",
    description:
      "Contact PawTenant for ESA letter support: call (409) 965-5885, email hello@pawtenant.com, or use our form. Mon–Fri, 7am–6pm CT.",
  },
  "/renew-esa-letter": {
    title: "Renew Your ESA Letter | PawTenant",
    description:
      "ESA letters expire after 12 months. Renew your emotional support animal letter with a licensed therapist in 2026. Fast delivery and a refund if you don't qualify. From $115.",
  },
  "/join-our-network": {
    title: "Join PawTenant's Licensed Provider Network | PawTenant",
    description:
      "Licensed clinicians: join PawTenant's ESA provider network reviewing ESA and PSD requests, with flexible case acceptance and full admin support.",
  },
  "/psd-assessment": {
    title: "PSD Letter Assessment | Start Online | PawTenant",
    description:
      "Get a legitimate Psychiatric Service Dog (PSD) letter from a licensed mental health professional. ADA compliant, HIPAA secure, delivered within 24 hours. Start your free PSD assessment.",
  },
  "/resource-center": {
    title: "ESA & PSD Resource Center | PawTenant",
    description:
      "The PawTenant ESA & PSD resource center: guides on state laws, housing rights, travel policies, and landlord verification in one place.",
  },
  "/esa-letter-verification": {
    title: "ESA Letter Verification for Landlords | PawTenant",
    description:
      "Landlords and property managers: verify a PawTenant ESA letter in seconds. Privacy-safe verification confirms provider license status without exposing medical information.",
  },
  "/sitemap": {
    title: "Sitemap | PawTenant",
    description:
      "Full sitemap of PawTenant — browse ESA letter guides, state pages, housing rights, PSD resources, blog posts, and more.",
  },
  "/everything-you-need-to-know-about-obtaining-an-esa-letter-online": {
    title: "Obtaining an ESA Letter Online: Full Guide | PawTenant",
    description:
      "Obtaining an ESA letter online: who qualifies, how licensed evaluations work, what makes a letter housing-ready, and how to spot fake sites.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | PawTenant",
    description:
      "Read PawTenant's privacy policy. We protect your personal health information under HIPAA and explain how we collect, use, and safeguard your data.",
  },
  "/terms-of-use": {
    title: "Terms of Use | PawTenant",
    description:
      "PawTenant's terms of use. Understand the terms and conditions that apply when using our ESA letter services and website.",
  },
  "/refund-policy": {
    title: "Refund Policy | PawTenant",
    description:
      "PawTenant's refund policy: a full refund if a licensed provider finds you don't qualify, plus an evidence-based review of housing-denial claims.",
  },
};

// ── Per-state ESA letter meta (copied from state-esa/page.tsx) ───────────────
// Each state page canonical: /esa-letter/:state
export const ESA_STATE_META: Record<string, SEOEntry> = {
  alabama: {
    title: "ESA Letter Alabama | Licensed LMHP | PawTenant",
    description:
      "Get a legitimate ESA letter in Alabama from a state-licensed mental health professional. Protect your housing rights under the Fair Housing Act.",
  },
  alaska: {
    title: "ESA Letter Alaska | Valid for Housing | PawTenant",
    description:
      "Need an ESA letter in Alaska? Connect with a licensed LMHP and receive valid ESA documentation for housing. HIPAA-secure, fully compliant with Alaska law.",
  },
  arizona: {
    title: "ESA Letter Arizona | Licensed Professionals | PawTenant",
    description:
      "Get a legitimate ESA letter in Arizona. Our licensed mental health professionals evaluate your needs and issue housing-compliant ESA documentation fast.",
  },
  arkansas: {
    title: "ESA Letter Arkansas | 30-Day Rule Explained | PawTenant",
    description:
      "Arkansas requires a 30-day provider relationship before an ESA letter can be issued. PawTenant connects you with licensed Arkansas LMHPs.",
  },
  california: {
    title: "ESA Letter California | CA-Licensed Therapists | PawTenant",
    description:
      "Get a legitimate ESA letter in California from a state-licensed therapist. California law requires a 30-day relationship. PawTenant ensures full compliance.",
  },
  colorado: {
    title: "ESA Letter Colorado | Valid for Housing | PawTenant",
    description:
      "Get a legitimate ESA letter in Colorado from a licensed mental health professional. No waiting period. Housing-compliant documentation delivered digitally.",
  },
  connecticut: {
    title: "ESA Letter Connecticut | Licensed LMHP | PawTenant",
    description:
      "Need an ESA letter in Connecticut? PawTenant connects you with state-licensed professionals for a proper evaluation. Receive valid ESA letter fast.",
  },
  delaware: {
    title: "ESA Letter Delaware | Housing-Compliant | PawTenant",
    description:
      "Get your ESA letter in Delaware from a licensed mental health professional. PawTenant ensures your documentation is FHA-compliant.",
  },
  florida: {
    title: "ESA Letter Florida | Legitimate & Legal | PawTenant",
    description:
      "Get a legitimate ESA letter in Florida from a an LMHP. Florida penalizes fake ESA letters. PawTenant ensures every letter is issued through a proper evaluation.",
  },
  georgia: {
    title: "ESA Letter Georgia | Licensed Therapists | PawTenant",
    description:
      "Need an ESA letter in Georgia? Connect with a state-licensed mental health professional through PawTenant. Valid for housing under the Fair Housing Act.",
  },
  hawaii: {
    title: "ESA Letter Hawaii | Valid Housing Documentation | PawTenant",
    description:
      "Get a legitimate ESA letter in Hawaii from a licensed LMHP. PawTenant provides housing-compliant ESA documentation valid across all Hawaii housing types.",
  },
  idaho: {
    title: "ESA Letter Idaho | Licensed Professionals | PawTenant",
    description:
      "Receive your ESA letter in Idaho from a state-licensed mental health professional. PawTenant ensures full FHA compliance so landlord recognizes your letter.",
  },
  illinois: {
    title: "ESA Letter Illinois | Verify Your LMHP | PawTenant",
    description:
      "landlords may verify your LMHP's license before accepting an ESA letter in Illinois. PawTenant only works with fully verifiable, state-licensed professionals.",
  },
  indiana: {
    title: "ESA Letter Indiana | Fast & Compliant | PawTenant",
    description:
      "Get ESA letter in Indiana from a licensed mental health professional. PawTenant's process is fast, HIPAA-secure, and fully compliant with Indiana housing law.",
  },
  iowa: {
    title: "ESA Letter Iowa | 30-Day Rule | Licensed LMHP | PawTenant",
    description:
      "Iowa requires a 30-day relationship with your LMHP before issuing an ESA letter. PawTenant helps you start the process early so it takes short time.",
  },
  kansas: {
    title: "ESA Letter Kansas | Housing Rights | PawTenant",
    description:
      "Get a legitimate ESA letter in Kansas from a licensed professional. PawTenant ensures your ESA documentation protects your housing rights.",
  },
  kentucky: {
    title: "ESA Letter Kentucky | Licensed & Legitimate | PawTenant",
    description:
      "Need an ESA letter in Kentucky? PawTenant connects you with a state-licensed LMHP for a thorough evaluation and delivers your ESA letter digitally.",
  },
  louisiana: {
    title: "ESA Letter in Louisiana | Licensed Providers | PawTenant",
    description:
      "Get a legitimate ESA letter in Louisiana. PawTenant works with licensed Louisiana mental health professionals to issue FHA-compliant documentation.",
  },
  maine: {
    title: "ESA Letter Maine | Valid for Housing | PawTenant",
    description:
      "Get your ESA letter in Maine from a licensed mental health professional. PawTenant ensures your letter meets FHA standards so landlords accept it statewide.",
  },
  maryland: {
    title: "ESA Letter Maryland | Licensed Therapists | PawTenant",
    description:
      "Get your ESA letter in Maryland from a state-licensed LMHP. PawTenant's housing-compliant ESA letter protects you from unfair pet fees and no-pet policies.",
  },
  massachusetts: {
    title: "ESA Letter Massachusetts | Legitimate & Fast | PawTenant",
    description:
      "Need an ESA letter in Massachusetts? PawTenant connects you with licensed MA mental health professionals for a compliant evaluation.",
  },
  michigan: {
    title: "ESA Letter Michigan | Licensed LMHP | PawTenant",
    description:
      "Get a legitimate ESA letter in Michigan from an LMHP. PawTenant provides fast, housing-compliant ESA documentation valid across all of Michigan.",
  },
  minnesota: {
    title: "ESA Letter Minnesota | Housing Compliant | PawTenant",
    description:
      "Receive a legitimate ESA letter in Minnesota from a state-licensed LMHP. PawTenant ensures full compliance with FHA so your ESA letter is accepted easily.",
  },
  mississippi: {
    title: "ESA Letter Mississippi | Valid for Housing | PawTenant",
    description:
      "Get your ESA letter in Mississippi from a licensed mental health professional. PawTenant delivers HIPAA-secure, FHA-compliant documentation. Apply online.",
  },
  missouri: {
    title: "ESA Letter Missouri | Licensed Professionals | PawTenant",
    description:
      "Need an ESA letter in Missouri? PawTenant connects you with state-licensed LMHPs for evaluation and fast digital delivery. Housing-compliant and FHA-valid.",
  },
  montana: {
    title: "ESA Letter Montana | Legitimate & Compliant | PawTenant",
    description:
      "Get a legitimate ESA letter in Montana from a licensed LMHP. PawTenant ensures your letter protects your housing rights under the Fair Housing Act statewide.",
  },
  nebraska: {
    title: "ESA Letter Nebraska | Housing Rights | PawTenant",
    description:
      "Receive a legitimate ESA letter in Nebraska from a licensed professional. PawTenant's process is compliant with Nebraska and federal housing requirements.",
  },
  nevada: {
    title: "ESA Letter Nevada | Licensed LMHP | PawTenant",
    description:
      "Get your ESA letter in Nevada from a state-licensed mental health professional. PawTenant ensures FHA-compliant letter so landlords accept it easily.",
  },
  "new-hampshire": {
    title: "ESA Letter New Hampshire | Fast & Legitimate | PawTenant",
    description:
      "Get an ESA letter in New Hampshire after an evaluation with a professional licensed in New Hampshire. Housing-focused, verifiable, refund if you don't qualify.",
  },
  "new-jersey": {
    title: "ESA Letter New Jersey | Verify Your Provider | PawTenant",
    description:
      "Landlords may verify your provider's license before accepting an ESA letter in New Jersey. PawTenant only uses verifiable, state-licensed NJ professionals.",
  },
  "new-mexico": {
    title: "ESA Letter New Mexico | Licensed Therapists | PawTenant",
    description:
      "Get a legitimate ESA letter in New Mexico from an LMHP. PawTenant provides housing-compliant ESA letter that protects your rights as a tenant in NM.",
  },
  "new-york": {
    title: "ESA Letter New York | Licensed NY Therapist | PawTenant",
    description:
      "New York landlords are encouraged to verify LMHP license status. PawTenant works only with verifiable NY-licensed professionals. Get your ESA letter today.",
  },
  "north-carolina": {
    title: "ESA Letter North Carolina | Housing Compliant | PawTenant",
    description:
      "Get a legitimate ESA letter in North Carolina from an LMHP. PawTenant ensures FHA-compliant documentation so you can live with your ESA easily.",
  },
  "north-dakota": {
    title: "ESA Letter North Dakota | Legitimate & Fast | PawTenant",
    description:
      "Receive a legitimate ESA letter in North Dakota from a licensed mental health professional. PawTenant delivers housing-compliant ESA letter. Apply now.",
  },
  ohio: {
    title: "ESA Letter Ohio | Licensed LMHP | PawTenant",
    description:
      "Get your ESA letter in Ohio from a state-licensed LMHP. PawTenant ensures your letter is FHA-compliant and accepted by Ohio landlords. Apply today.",
  },
  oklahoma: {
    title: "ESA Letter Oklahoma | Valid for Housing | PawTenant",
    description:
      "Get a legitimate ESA letter in Oklahoma from a licensed LMHP. PawTenant follows FHA and Oklahoma law to issue ESA letter. Apply online in minutes.",
  },
  oregon: {
    title: "ESA Letter Oregon | Licensed Professionals | PawTenant",
    description:
      "Need an ESA letter in Oregon? PawTenant connects you with a state-licensed LMHP for a proper evaluation. Receive valid, FHA-compliant ESA documentation.",
  },
  pennsylvania: {
    title: "ESA Letter Pennsylvania | Licensed Therapists | PawTenant",
    description:
      "Get a legitimate ESA letter in Pennsylvania from an LMHP. PawTenant delivers housing-compliant documentation accepted by PA landlords. Apply now.",
  },
  "rhode-island": {
    title: "ESA Letter Rhode Island | Fast & Compliant | PawTenant",
    description:
      "Receive your ESA letter in Rhode Island from a state-licensed LMHP. PawTenant ensures fully compliant ESA letter that protects your housing rights in RI.",
  },
  "south-carolina": {
    title: "ESA Letter South Carolina | Housing Rights | PawTenant",
    description:
      "Get a legitimate ESA letter in South Carolina from a licensed mental health professional. PawTenant ensures FHA-compliant documentation.",
  },
  "south-dakota": {
    title: "ESA Letter South Dakota | Licensed LMHP | PawTenant",
    description:
      "Need an ESA letter in South Dakota? PawTenant connects you with a licensed SD professional for a proper evaluation. Apply now.",
  },
  tennessee: {
    title: "ESA Letter Tennessee | Legitimate & Legal | PawTenant",
    description:
      "Get a legitimate ESA letter in Tennessee from a licensed LMHP. PawTenant ensures your letter meets FHA standards so you can live with your ESA easily.",
  },
  texas: {
    title: "ESA Letter Texas | TX-Licensed Professionals | PawTenant",
    description:
      "Get a legitimate ESA letter in Texas from a state-licensed mental health professional. No waiting period in TX. Housing-compliant and digitally delivered.",
  },
  utah: {
    title: "ESA Letter Utah | Valid for Housing | PawTenant",
    description:
      "Receive a legitimate ESA letter in Utah from a licensed LMHP. PawTenant ensures your documentation is fully FHA-compliant and accepted by Utah landlords.",
  },
  vermont: {
    title: "ESA Letter Vermont | Licensed & Compliant | PawTenant",
    description:
      "Get your ESA letter in Vermont from a state-licensed mental health professional. PawTenant delivers ESA documentation fast and securely. Apply today.",
  },
  virginia: {
    title: "ESA Letter Virginia | Licensed Therapists | PawTenant",
    description:
      "Get an ESA letter in Virginia after an evaluation with a professional licensed in Virginia. Housing-focused, verifiable, refund if you don't qualify.",
  },
  washington: {
    title: "ESA Letter Washington State | Licensed LMHP | PawTenant",
    description:
      "Get a legitimate ESA letter in Washington State. PawTenant works with licensed WA mental health professionals to issue FHA-compliant documentation.",
  },
  "washington-dc": {
    title: "ESA Letter Washington DC | Housing Compliant | PawTenant",
    description:
      "Get a legitimate ESA letter in Washington DC from a licensed LMHP. PawTenant ensures full FHA compliance so your ESA letter is accepted easily. Apply now.",
  },
  "west-virginia": {
    title: "ESA Letter West Virginia | Fast & Legitimate | PawTenant",
    description:
      "Receive your ESA letter in West Virginia from a licensed LMHP. PawTenant ensures FHA-compliant ESA documentation delivered digitally. Apply today.",
  },
  wisconsin: {
    title: "ESA Letter Wisconsin | Licensed Professionals | PawTenant",
    description:
      "Get a legitimate ESA letter in Wisconsin from a licensed LMHP. PawTenant delivers fast, housing-compliant ESA letter accepted statewide. Apply now.",
  },
  wyoming: {
    title: "ESA Letter Wyoming | Valid for Housing | PawTenant",
    description:
      "Need an ESA letter in Wyoming? PawTenant connects you with a state-licensed professional for a proper evaluation. FHA-compliant ESA letter delivered fast.",
  },
};

// ── PSD state meta: formulaic per state (matches state-psd/page.tsx logic) ───
// Human-readable state name lookup (from ESA slug list) for PSD title format.
const STATE_DISPLAY_NAMES: Record<string, string> = {
  alabama: "Alabama",
  alaska: "Alaska",
  arizona: "Arizona",
  arkansas: "Arkansas",
  california: "California",
  colorado: "Colorado",
  connecticut: "Connecticut",
  delaware: "Delaware",
  florida: "Florida",
  georgia: "Georgia",
  hawaii: "Hawaii",
  idaho: "Idaho",
  illinois: "Illinois",
  indiana: "Indiana",
  iowa: "Iowa",
  kansas: "Kansas",
  kentucky: "Kentucky",
  louisiana: "Louisiana",
  maine: "Maine",
  maryland: "Maryland",
  massachusetts: "Massachusetts",
  michigan: "Michigan",
  minnesota: "Minnesota",
  mississippi: "Mississippi",
  missouri: "Missouri",
  montana: "Montana",
  nebraska: "Nebraska",
  nevada: "Nevada",
  "new-hampshire": "New Hampshire",
  "new-jersey": "New Jersey",
  "new-mexico": "New Mexico",
  "new-york": "New York",
  "north-carolina": "North Carolina",
  "north-dakota": "North Dakota",
  ohio: "Ohio",
  oklahoma: "Oklahoma",
  oregon: "Oregon",
  pennsylvania: "Pennsylvania",
  "rhode-island": "Rhode Island",
  "south-carolina": "South Carolina",
  "south-dakota": "South Dakota",
  tennessee: "Tennessee",
  texas: "Texas",
  utah: "Utah",
  vermont: "Vermont",
  virginia: "Virginia",
  washington: "Washington",
  "washington-dc": "Washington DC",
  "west-virginia": "West Virginia",
  wisconsin: "Wisconsin",
  wyoming: "Wyoming",
};

// Differentiated prerender meta for the feature PSD states. Each carries a
// state-specific hook so the static <head> is unique per page. Compliance: no
// "guaranteed", "instant", "certified/registered", "same-day", or "100%
// money-back" language — a PSD letter documents a licensed provider evaluation.
const PSD_STATE_META_OVERRIDES: Record<string, SEOEntry> = {
  california: {
    title: "PSD Letter in California | Psychiatric Service Dog",
    description:
      "Get a PSD letter in California through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
  texas: {
    title: "PSD Letter in Texas: Psychiatric Service Dog | PawTenant",
    description:
      "Get a PSD letter in Texas through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
  florida: {
    title: "PSD Letter in Florida: Psychiatric Service Dog | PawTenant",
    description:
      "Get a PSD letter in Florida through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
  "new-york": {
    title: "PSD Letter in New York: Psychiatric Service Dog | PawTenant",
    description:
      "Get a PSD letter in New York through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
  "north-carolina": {
    title: "PSD Letter in North Carolina | Psychiatric Service Dog",
    description:
      "Get a PSD letter in North Carolina through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
  pennsylvania: {
    title: "PSD Letter in Pennsylvania | Psychiatric Service Dog",
    description:
      "Get a PSD letter in Pennsylvania through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
  ohio: {
    title: "PSD Letter in Ohio: Psychiatric Service Dog | PawTenant",
    description:
      "Psychiatric Service Dog (PSD) documentation in Ohio via a licensed provider evaluation. PSD vs ESA, Ohio Civil Rights Act context, refund if you don't qualify.",
  },
  georgia: {
    title: "PSD Letter in Georgia: Psychiatric Service Dog | PawTenant",
    description:
      "Get a PSD letter in Georgia through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
  illinois: {
    title: "PSD Letter in Illinois: Psychiatric Service Dog | PawTenant",
    description:
      "Get a PSD letter in Illinois through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
  arizona: {
    title: "PSD Letter in Arizona: Psychiatric Service Dog | PawTenant",
    description:
      "Get a PSD letter in Arizona through a licensed provider evaluation. How a psychiatric service dog differs from an ESA. Refund if you don't qualify.",
  },
};

function buildPSDEntry(slug: string): SEOEntry | null {
  if (PSD_STATE_META_OVERRIDES[slug]) return PSD_STATE_META_OVERRIDES[slug];
  const name = STATE_DISPLAY_NAMES[slug];
  if (!name) return null;
  return {
    title: `PSD Letter in ${name} | Psychiatric Service Dog Evaluation | PawTenant`,
    description: `Psychiatric Service Dog (PSD) documentation in ${name} through a licensed provider evaluation. Learn how a PSD differs from an ESA and what the letter can and can't do. Refund if you don't qualify.`,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function normalizePath(pathname: string): string {
  if (!pathname) return "/";
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function extractSegment(pathname: string, prefix: string): string | null {
  const normalized = normalizePath(pathname);
  if (!normalized.startsWith(prefix)) return null;
  const tail = normalized.slice(prefix.length).replace(/^\/+/, "");
  if (!tail || tail.includes("/")) return null;
  return tail.toLowerCase();
}

/**
 * Resolve the SEO entry for a pathname. Returns null if the pathname is a
 * dynamic route not covered here (page component should set its own).
 */
export function getSEO(pathname: string): SEOEntry | null {
  const path = normalizePath(pathname);

  if (SEO_AUDIT_METADATA[path]) return SEO_AUDIT_METADATA[path];

  if (CORE_PAGE_META[path]) return CORE_PAGE_META[path];

  const esaSlug = extractSegment(path, "/esa-letter");
  if (esaSlug && ESA_STATE_META[esaSlug]) return ESA_STATE_META[esaSlug];

  const psdSlug = extractSegment(path, "/psd-letter");
  if (psdSlug) return buildPSDEntry(psdSlug);

  return null;
}

/**
 * Build a self-referencing canonical URL for a pathname.
 * Normalizes trailing slash and always uses the production BASE_URL.
 */
export function buildCanonical(pathname: string): string {
  const path = normalizePath(pathname);
  const canonicalPath = [
    "/how-to-get-esa-letter-online",
    "/blog/how-to-get-an-esa-letter-online",
    "/everything-you-need-to-know-about-obtaining-an-esa-letter-online",
  ].includes(path) ? "/how-to-get-esa-letter" : path;
  return `${BASE_URL}${canonicalPath}`;
}